import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from './error-handler.service';
import { CompanyService } from './company.service';
import { IRolesQueryParams, IRolesCountQueryParams, ICreateRoleBody } from '../../shared/models/roles.model';

@Injectable({
  providedIn: 'root'
})
export class RolesService {
  private readonly baseUrl = environment.baseBackendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private companyService: CompanyService
  ) {}

  /**
   * Obtiene Roles con parámetros de consulta OData
   */
  getRoles(params?: IRolesQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/Roles`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un Rol
   */
  createRole(body: ICreateRoleBody): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/odata/Roles`, body, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el conteo de Roles
   */
  getRolesCount(params?: IRolesCountQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/Roles/$count`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Construye los headers HTTP incluyendo el X-Company-Id
   */
  private buildHeaders(): HttpHeaders {
    const selectedCompany = this.companyService.selectedCompany$.value;
    let headers = new HttpHeaders();

    if (selectedCompany?.id) {
      headers = headers.set('X-Company-Id', selectedCompany.id.toString());
    }

    return headers;
  }

  private buildQueryParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return httpParams;
  }

  /**
   * Obtiene Permissions con parámetros de consulta OData (solo lectura)
   */
  getPermissions(params?: IRolesQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/Permissions`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualiza parcialmente un Role por clave (PATCH OData)
   * @param key ID del Role a actualizar
   * @param data Datos a actualizar
   */
  patchRoleByKey(key: number | string, data: Partial<ICreateRoleBody> & { Name?: string; IsAdmin?: boolean }): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/odata/Roles(${key})`, data, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina un Role por clave (DELETE OData)
   * @param key ID del Role a eliminar
   */
  deleteRoleByKey(key: number | string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/Roles(${key})`, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea o reemplaza una relación específica de un Role con Permission (PUT $ref)
   * @param key ID del Role
   * @param permissionId ID del Permission
   */
  putRolePermissionRef(key: number | string, permissionId: number): Observable<any> {
    const url = `${this.baseUrl}/odata/Roles/${key}/Permissions/${permissionId}/$ref`;
    return this.http.put<any>(url, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina una relación específica de un Role con Permission (DELETE $ref)
   * @param key ID del Role
   * @param permissionId ID del Permission
   */
  deleteRolePermissionRef(key: number | string, permissionId: number): Observable<any> {
    const url = `${this.baseUrl}/odata/Roles/${key}/Permissions/${permissionId}/$ref`;
    return this.http.delete<any>(url, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  putUserRolesRef(key: number, navigationProperty: string = 'Permissions', relatedKey: number): Observable<any> {
    const url = `${this.baseUrl}/odata/Roles/${key}/${navigationProperty}/${relatedKey}/$ref`;
    return this.http.put<any>(url, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }
  deleteRolesRelationRef(key: number, navigationProperty: string = 'Permissions', relatedKey: number): Observable<any> {
    const url = `${this.baseUrl}/odata/Roles/${key}/${navigationProperty}/${relatedKey}/$ref`;
    return this.http.delete<any>(url, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }
}


